import React from 'react';

/**
 * The portal's one filter control.
 *
 * It exists because there were briefly two. The coaching-flags dropdown was
 * styled under `.coaching-flags .flags-filter select`, so when the same markup
 * was reused above the chart it inherited nothing and fell back to the browser's
 * native select — two controls doing the same job, looking unrelated, on the
 * same page.
 *
 * The lesson is the scoping, not the CSS: a control styled by its PARENT cannot
 * be reused, because reuse means a different parent. This owns its own class,
 * and every filter in the app renders through it.
 */

export interface FilterOption {
  id: string;
  label: string;
}

interface FilterSelectProps {
  /** DOM id — the <label> needs it, so it is required rather than generated. */
  id: string;
  /** Control label, e.g. "Client". */
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  /** Sentence to the right explaining what the current selection shows. */
  hint?: React.ReactNode;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  hint
}) => (
  <div className="filter-row">
    <label htmlFor={id}>{label}</label>
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
    {hint && <span className="filter-hint">{hint}</span>}
  </div>
);

export default FilterSelect;
